<script setup lang="ts">
import MultiChartV3 from '@/components/charts/multi-chart-v3.vue';
import GraphCardActionsV3 from '@/components/Common/GraphCardActionsV3.vue';
import GraphCardHeaders from '@/components/Common/GraphCardHeaders.vue';
import RexCardV3 from '@/rex-components/RexCardV3.vue';
import { useGraphData } from '@/components/Common/useGraphData';
import { useGraphActions } from '@/components/Common/useGraphActions';
import { useStoreData } from '@/components/Common/useStoreData';
import { notificationError } from '@/utils/notification';
import { useI18n } from 'vue-i18n';
import {
  GetChartOptionsParamType,
  GraphTypes,
  GraphTypesEnum,
  ChartTypes,
  ParamsType,
  ChartTypesEnum,
  HeaderType,
  Aggregation,
} from '@/components/Common/graph.types';
import {
  respFormatter,
  getChartOptionsData,
  lineChartOptionsValues,
} from '@/components/Common/graph.config';
import { computed, ref, watch } from 'vue';

interface Props {
  title?: string;
  queryName: string;
  params?: ParamsType;
  chartOptionsData?: GetChartOptionsParamType;
  graphType?: GraphTypes;
  type?: ChartTypes;
  header?: HeaderType;
  metricName: string;
  serviceName: string;
  axisLabels: { x: string; y: string };
  aggregation?: Aggregation;
}

const props = withDefaults(defineProps<Props>(), {
  params: () => ({
    condition: {
      name: '',
      entity: {},
    },
    duration: {},
  }),
  title: '',
  graphType: GraphTypesEnum.LINE_CHART,
  chartOptionsData: () => lineChartOptionsValues,
  type: ChartTypesEnum.RANGE_AREA,
  header: () => ({}),
  aggregation: () => ({ req: false }),
});
const { t } = useI18n();

const { getDurationTime, isForecastEnable } = useStoreData();

const graphType = ref(props.graphType);
const aggregation = ref(props.aggregation);

const { data, isLoading, error, options, makegraphQLCall, headerValues } =
  useGraphData(
    props.queryName,
    props.params,
    getChartOptionsData(graphType.value),
    graphType,
    props.axisLabels,
    aggregation,
  );

makegraphQLCall(props.queryName, props.params, respFormatter(graphType.value));

watch([error], () => {
  if (error) {
    notificationError(error.value?.message || t('errWhileFetchingData'));
  }
});

watch([() => props.graphType, getDurationTime, () => props.serviceName], () => {
  graphType.value = props.graphType;
  makegraphQLCall(
    props.queryName,
    props.params,
    respFormatter(graphType.value),
  );
});

const multiChart: any = ref(null);

const displayExpand = computed(() => {
  return graphType.value === GraphTypesEnum.FORECAST_CHART;
});

const { handleZoomIn, handleZoomOut, handleReset } = useGraphActions(
  multiChart,
  graphType,
);
</script>

<template>
  <div style="width: 100%">
    <RexCardV3
      :loading="isLoading"
      :headStyle="{ textAlign: 'start', minHeight: '0' }"
      :bodyStyle="{
        height: '21.3rem',
        paddingLeft: '0.5rem',
        paddingRight: '0.5rem',
      }"
      class="graph-builder-v3-card"
    >
      <template #title>
        <div class="graph-builder-card-title-box">
          <div>
            <BaseIcon
              :name="header?.titlePrefixIcon"
              width="1em"
              height="1em"
              v-if="header?.titlePrefixIcon"
              class="header-icon-preifx"
            />
            {{ title }}
            <BaseIcon
              :name="header?.titleSuffixIcon"
              v-if="header?.titleSuffixIcon"
              class="header-icon-suffix"
              width="1rem"
              height="1rem"
            />
          </div>
          <GraphCardHeaders :header="header" :childQuery="headerValues" />
          <GraphCardActionsV3
            :title="title"
            :handleZoomOut="handleZoomOut"
            :handleZoomIn="handleZoomIn"
            :handleReset="handleReset"
            :displayExpand="displayExpand"
            :metricName="metricName"
            :params="params"
            :serviceName="serviceName"
            :axisLabels="axisLabels"
            :aggregation="aggregation"
          />
        </div>
      </template>
      <MultiChartV3
        ref="multiChart"
        :series="data"
        :chartOptions="options"
        :type="type"
      />
    </RexCardV3>
  </div>
</template>

<style lang="scss" scoped>
.graph-builder-v3-box {
  width: 100%;
  height: 100%;
}
.graph-builder-card-title-box {
  @include flex-center-justify(space-between);
}
.header-icon-suffix {
  margin-right: 0.5em;
  @include svg-fill($color-active-blue);
  vertical-align: super;
}
.graph-builder-v3-card {
  @include dropshadow-effect;
  border-radius: 1.0625em;
}
</style>
